import { FC } from 'react';
import Grid from '@mui/material/Grid';
import offeerImg from '../../media/offer.png';
import girlsImg from '../../media/girls.png';
import fieldImg from '../../media/field.jpg';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import useTranslations from '../global/translation/hooks/translation';
import { useThemeContext } from '../global/ThemeContext';
import { designTokens } from '../../theme/designTokens';

const Offer: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode } = useThemeContext();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;

  const processText = (text: string) => {
    const paragraphs = text.split('\n\n');
    return paragraphs
      .flatMap((paragraph) => {
        if (paragraph.includes('- ')) {
          return paragraph.split('\n');
        }
        return [paragraph];
      })
      .filter((line) => line.trim() !== '');
  };

  const offerTextTop = processText(translations.label.offerTextOne[currentLanguage]);
  const offerTextMid = processText(translations.label.offerTextTwo[currentLanguage]);

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        bgcolor: darkMode ? palette.background.elevated : 'transparent',
        py: isMobile ? 6 : 10,
        px: isMobile ? 2 : 4,
      }}
    >
      {/* Header */}
      <Grid item xs={12} mb={6}>
        <Typography
          variant="h4"
          fontWeight={300}
          sx={{
            color: darkMode ? '#FFFFFF' : palette.primary,
            textAlign: 'center',
            letterSpacing: '-0.5px',
            mb: 2,
          }}
        >
          {translations.menuItems.offer[currentLanguage]}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: darkMode ? '#CCCCCC' : '#555555',
            textAlign: 'center',
            fontSize: '15px',
          }}
        >
          {translations.label.offerIntro[currentLanguage]}
        </Typography>
      </Grid>

      {/* Section 1: Horse Work */}
      <Grid container spacing={isMobile ? 4 : 6} sx={{ maxWidth: '1200px', mx: 'auto', mb: 8 }}>
        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {offerTextTop.map((line, idx) => {
              const isListItem = line.trim().startsWith('- ');
              const content = isListItem ? line.trim().substring(2) : line;

              if (idx === 0 && !isListItem) {
                return (
                  <Typography
                    key={idx}
                    variant="h6"
                    fontWeight={400}
                    sx={{
                      color: darkMode ? '#FFFFFF' : '#000000',
                      mb: 3,
                      letterSpacing: '-0.3px',
                    }}
                  >
                    {content}
                  </Typography>
                );
              }

              return isListItem ? (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1.5,
                    pl: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      mt: 1,
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: darkMode ? '#CCCCCC' : '#555555',
                      lineHeight: 1.7,
                      fontSize: '15px',
                    }}
                  >
                    {content}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  key={idx}
                  variant="body2"
                  sx={{
                    color: darkMode ? '#CCCCCC' : '#555555',
                    lineHeight: 1.7,
                    mb: 2,
                    fontSize: '15px',
                  }}
                >
                  {content}
                </Typography>
              );
            })}
          </Box>
        </Grid>
        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {/* Vertical accent line */}
            <Box
              sx={{
                position: 'absolute',
                left: '-4px',
                top: '20%',
                width: '4px',
                height: '60%',
                bgcolor: theme.palette.primary.main,
                borderRadius: '2px',
                zIndex: 2,
              }}
            />
            <Box
              sx={{
                width: '100%',
                paddingTop: '60%',
                position: 'relative',
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                src={offeerImg}
                alt="Horse training"
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', mb: 8 }}>
        <Box
          sx={{
            border: `1px solid ${darkMode ? palette.border.default : 'rgba(20, 56, 125,0.12)'}`,
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: darkMode ? palette.background.default : '#FFFFFF',
            boxShadow: darkMode ? 'none' : '0 16px 34px rgba(20, 56, 125,0.08)',
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', pt: { xs: '62%', md: '42%' } }}>
            <Box
              component="img"
              src={fieldImg}
              alt="Outdoor training arena"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2, md: 2.5 } }}>
            <Typography
              variant="body2"
              sx={{
                color: darkMode ? '#CCCCCC' : '#555555',
                lineHeight: 1.7,
                textAlign: 'center',
              }}
            >
              {currentLanguage === 'pl'
                ? 'To nasza zewnętrzna przestrzeń treningowa dla koni i jeźdźców.'
                : 'This is our outdoor training ground for horses and riders.'}
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* Section 2: Rider Training */}
      <Grid container spacing={isMobile ? 4 : 6} sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {/* Bottom corner accent */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '40px',
                borderBottom: `3px solid ${theme.palette.primary.main}`,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                borderRadius: '0 0 0 8px',
                zIndex: 2,
              }}
            />
            <Box
              sx={{
                width: '100%',
                paddingTop: '100%',
                position: 'relative',
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                src={girlsImg}
                alt="Rider training"
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {offerTextMid.map((line, idx) => {
              const isListItem = line.trim().startsWith('- ');
              const content = isListItem ? line.trim().substring(2) : line;

              return isListItem ? (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1.5,
                    pl: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      mt: 1,
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: darkMode ? '#CCCCCC' : '#555555',
                      lineHeight: 1.7,
                      fontSize: '15px',
                    }}
                  >
                    {content}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  key={idx}
                  variant="body2"
                  sx={{
                    color: darkMode ? '#CCCCCC' : '#555555',
                    lineHeight: 1.7,
                    mb: 2,
                    fontSize: '15px',
                  }}
                >
                  {content}
                </Typography>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Offer;
